import React, { useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useApiQuery, useApiMutation } from '../../hooks/useApiQuery';
import api from '../../services/api';
import useToast from '../../hooks/useToast';
import { useSocket } from '../../context/SocketContext';
import {
  QrCodeIcon,
  CurrencyRupeeIcon,
  WalletIcon,
  CreditCardIcon,
  BoltIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

const QUICK_AMOUNTS = [100, 200, 500, 1000];

const WalletRecharge = () => {
  const { eventId } = useParams();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const socket = useSocket();

  const [userId, setUserId] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [selectedUser, setSelectedUser] = useState(null);
  const [checkingUser, setCheckingUser] = useState(false);

  // ---------- Users ----------
  const {
    data: eventUsers = [],
    isLoading: usersLoading,
    refetch: refetchUsers,
  } = useApiQuery(
    ['event-users', eventId],
    () => api.get(`/events/${eventId}/users`).then((res) => res.data),
    {
      enabled: !!eventId,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: 0,
      onError: () => showError('Failed to load users'),
    },
  );

  // ---------- Event ----------
  const { data: event, isLoading: eventLoading } = useApiQuery(
    ['event', eventId],
    () => api.get(`/events/${eventId}`).then((res) => res.data),
    {
      enabled: !!eventId,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: 0,
    },
  );

  // ---------- Recharge mutation ----------
  const rechargeMutation = useApiMutation(
    (data) => api.post('/wallet/recharge/event', data).then((res) => res.data),
    {
      onSuccess: (data, variables) => {
        showSuccess(
          `Successfully recharged ₹${variables.amount} to ${selectedUser?.name}`,
        );
        setSelectedUser((prev) => ({
          ...prev,
          walletBalance:
            data.userWalletBalance ||
            data.balance ||
            prev.walletBalance + parseFloat(variables.amount),
        }));
        setUserId('');
        setAmount('');
        setDescription('');
        setPaymentMethod('cash');
        setSelectedUser(null);
        refetchUsers();
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Recharge failed');
      },
    },
  );

  const isLoading = usersLoading || eventLoading;

  const paymentOptions = useMemo(
    () => [
      { value: 'cash', label: 'Cash' },
      { value: 'upi', label: 'UPI' },
      { value: 'card', label: 'Card' },
    ],
    [],
  );

  // ---------- Socket ----------
  React.useEffect(() => {
    if (!socket) return;
    const handleWalletUpdate = (data) => {
      if (data.userId) {
        setSelectedUser((prev) => {
          if (!prev || prev._id !== data.userId) return prev;
          return {
            ...prev,
            walletBalance:
              data.userWalletBalance || data.newBalance || prev.walletBalance,
          };
        });
      }
    };
    socket.on('walletUpdated', handleWalletUpdate);
    return () => socket.off('walletUpdated', handleWalletUpdate);
  }, [socket]);

  // ---------- Check user ----------
  const handleCheckUser = useCallback(async () => {
    if (!userId.trim()) {
      showError('Please enter or scan a User ID');
      return;
    }
    setCheckingUser(true);
    try {
      const trimmedId = userId.trim();
      let userData = eventUsers.find((u) => u.customId === trimmedId);
      if (!userData) userData = eventUsers.find((u) => u._id === trimmedId);
      if (!userData) userData = eventUsers.find((u) => u.email === trimmedId);
      if (!userData)
        userData = eventUsers.find(
          (u) => u.name?.toLowerCase() === trimmedId.toLowerCase(),
        );

      if (!userData) {
        showError('User not found in this event. Please check the ID.');
        setSelectedUser(null);
        return;
      }

      const walletResponse = await api.get(
        `/wallet/event/${eventId}/user/${encodeURIComponent(userData.customId)}`,
      );
      const eventBalance = Number(walletResponse.data?.balance) || 0;

      setSelectedUser({ ...userData, walletBalance: eventBalance });
    } catch (error) {
      console.error('Check user error:', error);
      showError(error.response?.data?.message || 'Failed to find user');
      setSelectedUser(null);
    } finally {
      setCheckingUser(false);
    }
  }, [userId, eventUsers, eventId, showError]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleCheckUser();
      }
    },
    [handleCheckUser],
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!selectedUser) return showError('Please check a user first');
      if (!amount || parseFloat(amount) <= 0)
        return showError('Please enter a valid amount');

      rechargeMutation.mutate({
        userId: selectedUser.mainUserId || selectedUser.customId,
        eventId,
        amount: parseFloat(amount),
        description: description || `Recharge for ${event?.name || 'event'}`,
        paymentMethod,
      });
    },
    [
      selectedUser,
      amount,
      description,
      paymentMethod,
      eventId,
      event,
      rechargeMutation,
      showError,
    ],
  );

  const isSubmitting = rechargeMutation.isPending;
  const currentBalance = Number(selectedUser?.walletBalance) || 0;
  const rechargeAmount = Number(amount) || 0;
  const newBalance = currentBalance + rechargeAmount;

  // ---------- Loading ----------
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 font-['Barlow']">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-[#708090] text-sm">Loading…</p>
        </div>
      </div>
    );
  }

  if (!eventId) {
    return (
      <div className="flex items-center justify-center h-64 font-['Barlow']">
        <div className="text-center">
          <p className="text-red-500 text-lg">Event not found</p>
          <button
            onClick={() => (window.location.href = '/events-manager')}
            className="mt-4 text-[#1672b9] hover:underline"
          >
            Go back to Events
          </button>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER — single stacked card
  // ============================================================
  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white border border-[#dae1e7] rounded-xl shadow-sm p-6 font-['Barlow']"
    >
      {/* ============ HEADER ============ */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="text-xl font-bold text-[#2b3445]">
            Recharge Wallet
          </h2>
          <p className="text-sm text-[#708090] mt-1">
            Add funds to an attendee's wallet
          </p>
        </div>

        {/* Current Balance pill */}
        <div className="flex items-center gap-2 bg-[#f6f9fc] border border-[#dae1e7] rounded-xl px-3 py-2 shrink-0">
          <div className="w-9 h-9 rounded-lg bg-[#e8f1fb] flex items-center justify-center">
            <WalletIcon className="w-4 h-4 text-[#1672b9]" />
          </div>
          <div>
            <p className="text-[10px] text-[#708090]">Current Balance</p>
            <p className="text-base font-bold text-[#2b3445]">
              ₹{currentBalance.toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      {/* ============ FIND ATTENDEE ============ */}
      <div className="mb-5">
        <h3 className="text-sm font-semibold text-[#2b3445]">
          Find Attendee
        </h3>
        <p className="text-xs text-[#708090] mt-0.5 mb-3">
          Scan the band/card or enter user ID, email, or name
        </p>

        {/* Input + Check */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <QrCodeIcon className="h-5 w-5 text-[#708090]" />
            </div>
            <input
              type="text"
              value={userId}
              onChange={(e) => {
                setUserId(e.target.value);
                if (selectedUser) setSelectedUser(null);
              }}
              onKeyDown={handleKeyDown}
              className="w-full border border-[#dae1e7] rounded-lg pl-10 pr-3 py-3 text-sm text-[#2b3445] focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9]"
              placeholder="Enter or scan user ID"
              disabled={checkingUser || isSubmitting}
              autoFocus
            />
          </div>
          <button
            type="button"
            onClick={handleCheckUser}
            disabled={checkingUser || !userId.trim() || isSubmitting}
            className="bg-[#1672b9] text-white px-8 py-3 rounded-lg text-sm font-semibold hover:bg-[#0f5a9e] transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {checkingUser ? 'Checking…' : 'Check'}
          </button>
        </div>
      </div>

      {/* ============ ATTENDEE DETAILS (or placeholder) ============ */}
      {selectedUser ? (
        <div className="bg-[#f6f9fc] border border-[#dae1e7] rounded-xl p-4 mb-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#e8f1fb] flex items-center justify-center shrink-0">
              <UserIcon className="w-6 h-6 text-[#1672b9]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-semibold text-[#2b3445] truncate">
                {selectedUser.name}
              </p>
              <p className="text-xs text-[#708090] mt-0.5">
                {selectedUser.customId || selectedUser._id}
                {selectedUser.email && ` · ${selectedUser.email}`}
              </p>
            </div>

            <div className="hidden sm:flex items-center gap-3 shrink-0 pl-4 border-l border-[#dae1e7]">
              <div className="w-10 h-10 rounded-lg bg-[#e8f1fb] flex items-center justify-center">
                <WalletIcon className="w-5 h-5 text-[#1672b9]" />
              </div>
              <div>
                <p className="text-[10px] text-[#708090]">
                  Current Balance
                </p>
                <p className="text-lg font-bold text-[#2b3445]">
                  ₹{currentBalance.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-[#f6f9fc] border border-[#dae1e7] rounded-xl p-4 mb-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#e8f1fb] flex items-center justify-center shrink-0">
              <UserIcon className="w-6 h-6 text-[#1672b9]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#2b3445]">
                No attendee selected
              </p>
              <p className="text-xs text-[#708090] mt-0.5">
                Enter or scan an attendee ID to view their wallet balance.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ============ AMOUNT ============ */}
      <div className="mb-4">
        <label className="block text-sm font-semibold text-[#2b3445] mb-2">
          Amount (₹)
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <CurrencyRupeeIcon className="h-5 w-5 text-[#1672b9]" />
          </div>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full border-2 border-[#1672b9] rounded-lg pl-10 pr-3 py-3 text-sm font-medium text-[#2b3445] focus:outline-none focus:ring-2 focus:ring-[#1672b9]/30"
            placeholder="Enter amount"
            required
            disabled={!selectedUser || isSubmitting}
          />
        </div>
      </div>

      {/* ============ QUICK AMOUNTS ============ */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        {QUICK_AMOUNTS.map((amt) => {
          const isActive = Number(amount) === amt;
          return (
            <button
              key={amt}
              type="button"
              onClick={() => setAmount(String(amt))}
              disabled={!selectedUser || isSubmitting}
              className={`py-3 rounded-lg text-sm font-semibold border-2 transition-colors ${
                isActive
                  ? 'bg-[#e8f1fb] border-[#1672b9] text-[#1672b9]'
                  : 'bg-white border-[#dae1e7] text-[#2b3445] hover:border-[#1672b9]/50'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              ₹{amt.toLocaleString('en-IN')}
            </button>
          );
        })}
      </div>

      {/* ============ PAYMENT METHOD ============ */}
      <div className="mb-5">
        <label className="block text-sm font-semibold text-[#2b3445] mb-2">
          Payment Method
        </label>
        <div className="grid grid-cols-3 gap-3">
          {paymentOptions.map((opt) => {
            const isActive = paymentMethod === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setPaymentMethod(opt.value)}
                disabled={!selectedUser || isSubmitting}
                className={`relative py-3 px-4 rounded-lg border-2 text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                  isActive
                    ? 'bg-[#e8f1fb] border-[#1672b9] text-[#1672b9]'
                    : 'bg-white border-[#dae1e7] text-[#2b3445] hover:border-[#1672b9]/50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <CreditCardIcon
                  className={`w-5 h-5 ${
                    isActive ? 'text-[#1672b9]' : 'text-[#708090]'
                  }`}
                />
                <span>{opt.label}</span>

                {isActive && (
                  <span className="absolute right-3 w-5 h-5 rounded-full bg-[#1672b9] flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ============ NEW BALANCE PREVIEW + SUBMIT ============ */}
      <div className="bg-[#e8f8ee] border border-green-100 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-11 h-11 rounded-full bg-green-100 flex items-center justify-center shrink-0">
            <WalletIcon className="w-5 h-5 text-green-700" />
          </div>
          <div>
            <p className="text-xs text-[#708090]">New Wallet Balance</p>
            <p className="text-2xl font-bold text-green-700">
              ₹{newBalance.toFixed(2)}
            </p>
            <p className="text-xs text-[#708090] mt-0.5">
              Current ₹{currentBalance.toFixed(2)} + Recharge ₹
              {rechargeAmount.toFixed(2)}
            </p>
          </div>
        </div>

        <button
          type="submit"
          disabled={!selectedUser || !amount || isSubmitting}
          className="bg-[#1672b9] text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-[#0f5a9e] transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap w-full sm:w-auto inline-flex items-center justify-center gap-2"
        >
          <BoltIcon className="w-4 h-4" />
          {isSubmitting ? 'Processing…' : 'Recharge Wallet'}
        </button>
      </div>
    </form>
  );
};

export default WalletRecharge;